import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { SopSyncBanner } from "./SopSyncBanner";

export interface SopFieldsValues {
  informationCollectionRequirements: string;
  secondaryInformationToRequest: string;
  quotingSop: string;
  itemNamingGuidance: string;
  itemSearchEnabled: boolean;
  itemSearchStrategy: string;
}

interface SopFieldsSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  values: SopFieldsValues;
  onApply: (values: SopFieldsValues) => void;
  /** Sync state for context, displayed in the sheet header */
  sopVersion: string | null;
  sopUpdatedBy: string | null;
  updatedAt: Date;
}

/**
 * Unified editor for the SOP-synced fields. Click "Apply" to commit all
 * five field values at once.
 */
export function SopFieldsSheet({
  open,
  onOpenChange,
  values,
  onApply,
  sopVersion,
  sopUpdatedBy,
  updatedAt,
}: SopFieldsSheetProps) {
  const [local, setLocal] = useState<SopFieldsValues>(values);

  useEffect(() => {
    if (open) {
      setLocal(values);
    }
  }, [open, values]);

  const handleApply = () => {
    onApply(local);
    onOpenChange(false);
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:w-[720px] sm:max-w-[720px] overflow-y-auto">
        <SheetHeader>
          <SheetTitle>Edit System-Managed Fields</SheetTitle>
          {sopVersion !== null && (
            <SheetDescription>
              Configured automatically. Manual edits made here will be
              overwritten on the next system update.
            </SheetDescription>
          )}
          <div className="pt-1">
            <SopSyncBanner
              sopVersion={sopVersion}
              sopUpdatedBy={sopUpdatedBy}
              updatedAt={updatedAt}
            />
          </div>
        </SheetHeader>

        <div className="space-y-6 py-4">
          <div className="space-y-2">
            <Label className="text-sm font-medium">
              Information Collection Requirements
            </Label>
            <p className="text-xs text-muted-foreground">
              Checklist the agent must collect before creating a quote.
            </p>
            <Textarea
              rows={6}
              value={local.informationCollectionRequirements}
              onChange={(e) =>
                setLocal({
                  ...local,
                  informationCollectionRequirements: e.target.value,
                })
              }
            />
          </div>

          <div className="space-y-2">
            <Label className="text-sm font-medium">
              Secondary Information to Request
            </Label>
            <p className="text-xs text-muted-foreground">
              Additional information to request from customers.
            </p>
            <Textarea
              rows={6}
              value={local.secondaryInformationToRequest}
              onChange={(e) =>
                setLocal({
                  ...local,
                  secondaryInformationToRequest: e.target.value,
                })
              }
            />
          </div>

          <div className="space-y-2">
            <Label className="text-sm font-medium">Quoting SOP</Label>
            <p className="text-xs text-muted-foreground">
              Standard operating procedure for the quoting workflow.
            </p>
            <Textarea
              rows={12}
              value={local.quotingSop}
              onChange={(e) =>
                setLocal({ ...local, quotingSop: e.target.value })
              }
            />
          </div>

          <div className="space-y-2">
            <Label className="text-sm font-medium">Item Naming Guidance</Label>
            <p className="text-xs text-muted-foreground">
              How the agent names items (name, description, and revision). Leave
              blank to use the default convention (name = part number,
              description = drawing title, revision in the revision field,
              tooling suffixed "- Tooling").
            </p>
            <Textarea
              rows={8}
              value={local.itemNamingGuidance}
              onChange={(e) =>
                setLocal({ ...local, itemNamingGuidance: e.target.value })
              }
            />
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <div>
                <Label className="text-sm font-medium">
                  Item Search Sub-Agent
                </Label>
                <p className="text-xs text-muted-foreground">
                  Dedicated sub-agent for catalog item searches.
                </p>
              </div>
              <Switch
                checked={local.itemSearchEnabled}
                onCheckedChange={(v) =>
                  setLocal({ ...local, itemSearchEnabled: v })
                }
              />
            </div>
          </div>

          {local.itemSearchEnabled && (
            <div className="space-y-2">
              <Label className="text-sm font-medium">
                Item Search Strategy
              </Label>
              <p className="text-xs text-muted-foreground">
                Custom search rules and item matching instructions.
              </p>
              <Textarea
                rows={10}
                value={local.itemSearchStrategy}
                onChange={(e) =>
                  setLocal({ ...local, itemSearchStrategy: e.target.value })
                }
              />
            </div>
          )}
        </div>

        <SheetFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleApply}>Apply</Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
